import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, useInView, useSpring } from "framer-motion";
import { 
  Wrench, Activity, Zap, Layers, Cpu, Settings, 
  FileText, Mail, Linkedin, Github, ChevronDown,
  GraduationCap, Briefcase, Code, MapPin, Calendar,
  ExternalLink, Menu, X, Shield, Award
} from "lucide-react";
import { usePortfolio } from "./context/PortfolioContext";

// Default Portfolio Data (fallback when API returns empty)
const defaultPortfolioData = {
  name: "Sreekanth Netha Thatikonda",
  title: "Hydraulic Systems Engineer",
  tagline: "Designing the future of Mobile Hydraulic Machines",
  email: "sthatiko@asu.edu",
  location: "Kansas, United States",
  linkedin: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/",
  about: `I am a passionate Mechanical and Aerospace Engineering graduate from Arizona State University. My fascination with engineering started during my undergraduate studies, where I developed a keen interest in data science through a project involving fluid conveyance negotiating in different mediums and analyzing the effects using multiple software tools. This experience ignited my passion for design, architecture, and data analysis.

I have honed my skills in various programming languages such as Python, R, and MATLAB, along with proficiency in design, simulation, and system tools. My professional experience includes working as a Hydraulics Systems Engineer at CNH Industrial, where I have been involved in the design, development, and detailing of hydraulic and pneumatic components and systems for skid steers and compact track loaders.

I am eager to leverage my engineering expertise to further my professional growth. I am excited to take on new challenges, implement innovative solutions, and contribute meaningfully to a dynamic team.`,
  education: [
    {
      institution: "Arizona State University",
      degree: "Master of Science in Mechanical and Aerospace Engineering",
      gpa: "GPA: 3.8/4.0",
      period: "January 2022 - December 2023",
      location: "Arizona, United States",
      courses: ["Design", "System Modelling", "Control Systems", "Machine Learning", "Vehicle Dynamics", "Data Visualization"]
    },
    {
      institution: "Rajasthan Technical University",
      degree: "Bachelor of Technology in Aeronautical Engineering",
      period: "August 2017 - July 2021",
      location: "Rajasthan, India",
      courses: ["Design Modelling", "Fluid Mechanics", "Aircraft Systems", "Computer Programming", "Aerodynamics", "Aircraft Structures"]
    },
    {
      institution: "School of Aeronautics",
      degree: "Bachelor of Science in Aircraft Maintenance Engineering - Avionics",
      period: "August 2017 - Dec 2020",
      location: "Rajasthan, India",
      courses: ["Electronics Module 5", "Materials and Hardware", "Aircraft Systems", "Digital Techniques", "Propulsion", "Maintenance Practices"]
    }
  ],
  experience: [
    {
      company: "CNH Industrial",
      role: "Hydraulic Systems Engineer",
      period: "Aug 2024 - Present",
      type: "Full-time",
      location: "Kansas, United States",
      highlights: [
        "Working on design, development, and detailing of Hydraulic and pneumatic components and systems for Skid Steers and Compact Track Loaders.",
        "Generate innovative SSL/CTL hydraulic system design for CPM and Next Gen to enhance vehicle performance, customer value, and cost reduction.",
        "Creating engineering specifications, work closely with suppliers, and adhere to engineering standards and CNHi GPD processes.",
        "Experience working with Creo, TeamCenter, Automation Studios, AmeSim software's to develop hydraulic system designs, layouts, modelling, and simulations."
      ]
    },
    {
      company: "Arizona State University",
      role: "Teaching Assistant",
      period: "June 2022 - July 2023",
      type: "Part-time",
      location: "Arizona, United States",
      highlights: [
        "Working as an Assistant Professor for Physics Lecture, Laboratory and played a pivotal role in instructing engineering and medical students.",
        "TA for Engineering Mechanics I: Statics and Dynamics",
        "Teaching students and grading exams for the concepts of Force systems, resultants, equilibrium using MATLAB."
      ]
    },
    {
      company: "School of Aeronautics",
      role: "Control Systems Engineer",
      period: "Jan 2021 - Dec 2021",
      type: "Full-time",
      location: "Rajasthan, India",
      highlights: [
        "Compliance with GD&T specifications, Designing, V&V testing and verification of mechanical and electrical systems using CAD Software's.",
        "Work Closely with the mechanical design and manufacturing team to develop and modify parts for gear drives.",
        "Performed inspections, diagnostics, and repair activities for mechanical and electrical designs."
      ]
    },
    {
      company: "Hindustan Aeronautics Ltd.",
      role: "Mechanical Design Engineer - Intern",
      period: "Jan 2019 - Dec 2019",
      type: "Internship",
      location: "Bangalore, India",
      highlights: [
        "Designed and Manufactured ALH DHRUV, CHETAK vehicle components of Helicopters according to EASA DGCA CAR PART 121 and 145 regulations.",
        "Designing and testing to validate structural designs of aircraft structures and FEA analysis via CAD software's.",
        "Expertly disassembled, inspected, repaired, and reassembled Auxiliary Power Units and airframe components."
      ]
    }
  ],
  projects: [
    {
      title: "Adaptive Powertrain System Design",
      description: "Advanced adaptive powertrain system using Python, Simulink, and MATLAB, with custom hardware interfaces.",
      tags: ["Python", "Simulink", "MATLAB", "Powertrain"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    },
    {
      title: "Steer-by-Wire System Design",
      description: "SBW system model with advanced control techniques and co-simulation with CARSIM.",
      tags: ["Control Systems", "CARSIM", "Simulation"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    },
    {
      title: "Design Simulations",
      description: "Geospatial data analysis from wind turbines using Python and MATLAB.",
      tags: ["Python", "MATLAB", "Data Analysis"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    },
    {
      title: "Analyzing HVAC Designs",
      description: "HVAC analysis using AutoCAD, Ansys Fluent for heat transfer efficiency.",
      tags: ["ANSYS", "CFD", "HVAC"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    },
    {
      title: "Power Optimization via Feedforward",
      description: "HVAC analysis using AutoCAD, Ansys Fluent for heat transfer efficiency.",
      tags: ["PID", "Optimization", "Control"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    },
    {
      title: "Fluidic Thrust Vector Control",
      description: "Developed fluidic thrust vector control technology achieving 12.4-degree deflection using ANSYS, CFX, CATIA, and SolidWorks.",
      tags: ["ANSYS", "CFX", "CATIA", "SolidWorks"],
      link: "https://www.linkedin.com/in/sreekanth-netha-thatikonda/details/projects/"
    }
  ],
  skills: {
    languages: ["Python", "MATLAB", "R", "HTML5", "CSS3", "JavaScript"],
    design: ["Creo", "Simulink", "AutoCAD", "DS", "Fusion 360", "ANSYS", "Automation Studio", "AmeSim"],
    databases: ["PTC Windchill", "Siemens Teamcenter"],
    libraries: ["Pandas", "NumPy", "Matplotlib", "Seaborn", "Scikit-learn", "SciPy", "TensorFlow", "PyTorch"],
    tools: ["Salesforce", "Git", "Jupyter", "Slack", "Tableau", "Power BI", "PyCharm", "VS Code", "Excel"]
  },
  certifications: []
};

// Background Images - Hydraulic Equipment Theme (optimized with lazy loading)
const HYDRAULIC_BG = "https://customer-assets.emergentagent.com/job_sreekanth-portfolio/artifacts/tsp1w9kd_unnamed.jpg";
const HERO_BG = HYDRAULIC_BG;
const EXPERIENCE_BG = HYDRAULIC_BG;
const WIREFRAME_BG = HYDRAULIC_BG;

// Preload critical images
const preloadImages = () => {
  const img = new Image();
  img.src = HYDRAULIC_BG;
};
if (typeof window !== 'undefined') {
  preloadImages();
}

// Hero Schematic without title (for background overlay)
const HeroSchematicOverlay = ({ scrollProgress = 0 }) => {
  const flowOffset = 200 - (scrollProgress * 200);
  
  return (
    <svg viewBox="0 0 500 400" className="w-full h-full">
      <defs>
        <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#12d640" />
          <stop offset="100%" stopColor="#00d4ff" />
        </linearGradient>
        <filter id="heroGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Labels only - no title */}
      <text x="60" y="360" fill="#666" fontSize="9" fontFamily="Space Mono">RESERVOIR</text>
      <text x="55" y="250" fill="#666" fontSize="9" fontFamily="Space Mono">PUMP</text>
      <text x="200" y="140" fill="#666" fontSize="9" fontFamily="Space Mono">CONTROL VALVE</text>
      <text x="380" y="95" fill="#666" fontSize="9" fontFamily="Space Mono">LIFT CYL</text>
      <text x="380" y="275" fill="#666" fontSize="9" fontFamily="Space Mono">TILT CYL</text>
      
      {/* Hydraulic Reservoir */}
      <rect x="40" y="300" width="80" height="50" fill="none" stroke="#12d640" strokeWidth="2" rx="4" />
      <line x1="50" y1="310" x2="110" y2="310" stroke="#12d640" strokeWidth="1" opacity="0.5" />
      <rect x="55" y="315" width="50" height="30" fill="#12d640" opacity="0.2" />
      
      {/* Hydraulic Pump - static */}
      <g>
        <circle cx="80" cy="230" r="25" fill="none" stroke="#12d640" strokeWidth="2" />
        <polygon points="80,210 95,240 65,240" fill="none" stroke="#12d640" strokeWidth="2" />
        <circle cx="80" cy="230" r="5" fill="#12d640" />
      </g>
      
      {/* Pump to Reservoir */}
      <path d="M80 255 L80 300" fill="none" stroke="#00d4ff" strokeWidth="3" strokeDasharray="8,4" opacity="0.6" />
      
      {/* Pressure Line - Pump to Control Valve */}
      <path 
        d="M105 230 L150 230 L150 180 L200 180" 
        fill="none" stroke="url(#heroGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset} filter="url(#heroGlow)"
      />
      
      {/* Control Valve */}
      <rect x="200" y="150" width="100" height="60" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="210" y="165" width="80" height="30" fill="#12d640" opacity="0.15" className="valve-animate" />
      <line x1="220" y1="180" x2="280" y2="180" stroke="#12d640" strokeWidth="2" />
      
      {/* Valve Ports */}
      <circle cx="220" cy="150" r="4" fill="#12d640" />
      <circle cx="250" cy="150" r="4" fill="#12d640" />
      <circle cx="280" cy="150" r="4" fill="#12d640" />
      <circle cx="220" cy="210" r="4" fill="#00d4ff" />
      <circle cx="280" cy="210" r="4" fill="#00d4ff" />
      
      {/* Valve to Lift Cylinder */}
      <path d="M280 150 L280 115 L360 115" fill="none" stroke="url(#heroGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset * 0.8} filter="url(#heroGlow)" />
      
      {/* Lift Cylinder */}
      <rect x="360" y="100" width="90" height="35" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="365" y="107" width="40" height="21" fill="#12d640" opacity="0.3" className="cylinder-animate" />
      <rect x="405" y="112" width="50" height="11" fill="none" stroke="#12d640" strokeWidth="2" />
      <circle cx="455" cy="117" r="6" fill="#12d640" opacity="0.5" />
      
      {/* Valve to Tilt Cylinder */}
      <path d="M250 210 L250 255 L360 255" fill="none" stroke="url(#heroGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset * 0.6} filter="url(#heroGlow)" />
      
      {/* Tilt Cylinder */}
      <rect x="360" y="240" width="90" height="35" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="365" y="247" width="35" height="21" fill="#12d640" opacity="0.3" className="cylinder-animate" style={{animationDelay: '0.5s'}} />
      <rect x="400" y="252" width="55" height="11" fill="none" stroke="#12d640" strokeWidth="2" />
      <circle cx="455" cy="257" r="6" fill="#12d640" opacity="0.5" />
      
      {/* Return Lines */}
      <path d="M220 210 L220 340 L120 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.6" />
      <path d="M365 135 L340 135 L340 340 L120 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
      <path d="M365 275 L340 275 L340 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
      
      {/* Flow Indicators */}
      <circle cx="130" cy="230" r="3" fill="#12d640" className="glow-animation" />
      <circle cx="175" cy="180" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.3s'}} />
      <circle cx="280" cy="130" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.5s'}} />
      <circle cx="320" cy="115" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.7s'}} />
      
      {/* Pressure Gauge - needle rotates inside circle */}
      <g>
        <circle cx="170" cy="200" r="15" fill="none" stroke="#12d640" strokeWidth="1.5" />
        <text x="164" y="204" fill="#12d640" fontSize="8" fontFamily="Space Mono">PSI</text>
        <g className="gauge-needle" style={{ transformOrigin: '170px 200px' }}>
          <line x1="170" y1="200" x2="170" y2="188" stroke="#12d640" strokeWidth="1.5" />
        </g>
      </g>
    </svg>
  );
};

// SSL/CTL Hydraulic Schematic
const SSLCTLSchematic = ({ scrollProgress = 0 }) => {
  const flowOffset = 200 - (scrollProgress * 200);
  
  return (
    <svg viewBox="0 0 500 400" className="w-full h-full">
      <defs>
        <linearGradient id="pressureGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#12d640" />
          <stop offset="100%" stopColor="#00d4ff" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Title */}
      <text x="250" y="25" fill="#12d640" fontSize="14" fontFamily="Space Mono" textAnchor="middle" fontWeight="bold">SSL/CTL HYDRAULIC SYSTEM</text>
      
      {/* Labels */}
      <text x="60" y="360" fill="#666" fontSize="9" fontFamily="Space Mono">RESERVOIR</text>
      <text x="55" y="250" fill="#666" fontSize="9" fontFamily="Space Mono">PUMP</text>
      <text x="200" y="140" fill="#666" fontSize="9" fontFamily="Space Mono">CONTROL VALVE</text>
      <text x="380" y="95" fill="#666" fontSize="9" fontFamily="Space Mono">LIFT CYL</text>
      <text x="380" y="275" fill="#666" fontSize="9" fontFamily="Space Mono">TILT CYL</text>
      
      {/* Hydraulic Reservoir */}
      <rect x="40" y="300" width="80" height="50" fill="none" stroke="#12d640" strokeWidth="2" rx="4" />
      <line x1="50" y1="310" x2="110" y2="310" stroke="#12d640" strokeWidth="1" opacity="0.5" />
      <rect x="55" y="315" width="50" height="30" fill="#12d640" opacity="0.2" />
      
      {/* Hydraulic Pump - static */}
      <g>
        <circle cx="80" cy="230" r="25" fill="none" stroke="#12d640" strokeWidth="2" />
        <polygon points="80,210 95,240 65,240" fill="none" stroke="#12d640" strokeWidth="2" />
        <circle cx="80" cy="230" r="5" fill="#12d640" />
      </g>
      
      {/* Pump to Reservoir */}
      <path d="M80 255 L80 300" fill="none" stroke="#00d4ff" strokeWidth="3" strokeDasharray="8,4" opacity="0.6" />
      
      {/* Pressure Line - Pump to Control Valve */}
      <path 
        d="M105 230 L150 230 L150 180 L200 180" 
        fill="none" stroke="url(#pressureGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset} filter="url(#glow)"
      />
      
      {/* Control Valve */}
      <rect x="200" y="150" width="100" height="60" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="210" y="165" width="80" height="30" fill="#12d640" opacity="0.15" className="valve-animate" />
      <line x1="220" y1="180" x2="280" y2="180" stroke="#12d640" strokeWidth="2" />
      
      {/* Valve Ports */}
      <circle cx="220" cy="150" r="4" fill="#12d640" />
      <circle cx="250" cy="150" r="4" fill="#12d640" />
      <circle cx="280" cy="150" r="4" fill="#12d640" />
      <circle cx="220" cy="210" r="4" fill="#00d4ff" />
      <circle cx="280" cy="210" r="4" fill="#00d4ff" />
      
      {/* Valve to Lift Cylinder */}
      <path d="M280 150 L280 115 L360 115" fill="none" stroke="url(#pressureGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset * 0.8} filter="url(#glow)" />
      
      {/* Lift Cylinder */}
      <rect x="360" y="100" width="90" height="35" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="365" y="107" width="40" height="21" fill="#12d640" opacity="0.3" className="cylinder-animate" />
      <rect x="405" y="112" width="50" height="11" fill="none" stroke="#12d640" strokeWidth="2" />
      <circle cx="455" cy="117" r="6" fill="#12d640" opacity="0.5" />
      
      {/* Valve to Tilt Cylinder */}
      <path d="M250 210 L250 255 L360 255" fill="none" stroke="url(#pressureGradient)" strokeWidth="3"
        strokeDasharray="20,10" strokeDashoffset={flowOffset * 0.6} filter="url(#glow)" />
      
      {/* Tilt Cylinder */}
      <rect x="360" y="240" width="90" height="35" fill="none" stroke="#12d640" strokeWidth="2" rx="2" />
      <rect x="365" y="247" width="35" height="21" fill="#12d640" opacity="0.3" className="cylinder-animate" style={{animationDelay: '0.5s'}} />
      <rect x="400" y="252" width="55" height="11" fill="none" stroke="#12d640" strokeWidth="2" />
      <circle cx="455" cy="257" r="6" fill="#12d640" opacity="0.5" />
      
      {/* Return Lines */}
      <path d="M220 210 L220 340 L120 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.6" />
      <path d="M365 135 L340 135 L340 340 L120 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
      <path d="M365 275 L340 275 L340 340" fill="none" stroke="#00d4ff" strokeWidth="2" strokeDasharray="8,4" opacity="0.4" />
      
      {/* Flow Indicators */}
      <circle cx="130" cy="230" r="3" fill="#12d640" className="glow-animation" />
      <circle cx="175" cy="180" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.3s'}} />
      <circle cx="280" cy="130" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.5s'}} />
      <circle cx="320" cy="115" r="3" fill="#12d640" className="glow-animation" style={{animationDelay: '0.7s'}} />
      
      {/* Pressure Gauge - needle rotates inside circle */}
      <g>
        <circle cx="170" cy="200" r="15" fill="none" stroke="#12d640" strokeWidth="1.5" />
        <text x="164" y="204" fill="#12d640" fontSize="8" fontFamily="Space Mono">PSI</text>
        <g className="gauge-needle" style={{ transformOrigin: '170px 200px' }}>
          <line x1="170" y1="200" x2="170" y2="188" stroke="#12d640" strokeWidth="1.5" />
        </g>
      </g>
    </svg>
  );
};

// Combined Schematics Component
const HydraulicSchematics = ({ scrollProgress = 0 }) => {
  return (
    <div className="w-full h-full flex flex-col">
      {/* Schematic Content */}
      <div className="flex-1">
        <SSLCTLSchematic scrollProgress={scrollProgress} />
      </div>
    </div>
  );
};

// Scroll-Triggered Flow Animation Component
const ScrollFlowAnimation = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const flowProgress = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const unsubscribe = flowProgress.on("change", (v) => setProgress(v));
    return () => unsubscribe();
  }, [flowProgress]);

  return (
    <div ref={ref} className="absolute inset-0 z-10 pointer-events-none opacity-30">
      <HeroSchematicOverlay scrollProgress={progress} />
    </div>
  );
};

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = ['About', 'Education', 'Experience', 'Projects', 'Skills', 'Certifications', 'Contact'];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#12d640]/20' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <motion.a
            href="#hero"
            className="font-heading text-2xl font-semibold tracking-wider gradient-text"
            whileHover={{ scale: 1.05 }}
            data-testid="nav-logo"
          >
            SK
          </motion.a>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <motion.a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="font-mono text-sm text-[#aaa] hover:text-[#12d640] transition-colors uppercase tracking-widest"
                whileHover={{ y: -2 }}
                data-testid={`nav-${item.toLowerCase()}`}
              >
                {item}
              </motion.a>
            ))}
            {/* Resume Link - Opens in new tab */}
            <motion.a
              href="/assets/resume/RESUME.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-sm text-[#12d640] hover:text-[#00ff55] transition-colors uppercase tracking-widest border border-[#12d640]/50 px-3 py-1 rounded hover:border-[#12d640] hover:bg-[#12d640]/10"
              whileHover={{ y: -2, scale: 1.05 }}
              data-testid="nav-resume"
            >
              Resume
            </motion.a>
            {/* Admin Link */}
            <motion.a
              href="/admin"
              className="font-mono text-sm text-[#666] hover:text-[#00d4ff] transition-colors uppercase tracking-widest"
              whileHover={{ y: -2 }}
              data-testid="nav-admin"
            >
              <Shield size={16} />
            </motion.a>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="md:hidden bg-[#0a0a0a]/98 backdrop-blur-md border-t border-[#12d640]/20"
        >
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="block font-mono text-sm text-[#aaa] hover:text-[#12d640] uppercase tracking-widest"
                onClick={() => setIsOpen(false)}
                data-testid={`mobile-nav-${item.toLowerCase()}`}
              >
                {item}
              </a>
            ))}
            {/* Resume Link for Mobile */}
            <a
              href="/assets/resume/RESUME.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="block font-mono text-sm text-[#12d640] uppercase tracking-widest border border-[#12d640]/50 px-3 py-2 rounded text-center hover:bg-[#12d640]/10"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-nav-resume"
            >
              Resume
            </a>
            {/* Admin Link for Mobile */}
            <a
              href="/admin"
              className="block font-mono text-sm text-[#666] hover:text-[#00d4ff] uppercase tracking-widest"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-nav-admin"
            >
              Admin
            </a>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

// Hero Section
const HeroSection = ({ data }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background - Cinematic CTL Image */}
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="CNH Compact Track Loader - Cinematic"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/40 to-[#0a0a0a]" />
      </motion.div>

      {/* Schematic Overlay */}
      <ScrollFlowAnimation />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p className="font-mono text-sm tracking-[0.3em] mb-4 uppercase gradient-text">
            {data.title}
          </p>
          <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-medium uppercase tracking-tight text-white mb-6">
            {data.name.split(' ').map((word, i) => (
              <span key={i} className="block">
                <motion.span
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className={i === 0 ? "gradient-text" : ""}
                >
                  {word}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-[#aaa] text-lg sm:text-xl max-w-2xl mx-auto mb-8 font-light"
          >
            {data.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.a
              href="#contact"
              className="inline-flex items-center gap-2 px-8 py-3 font-mono text-sm uppercase tracking-widest font-medium text-black rounded-lg"
              style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="hero-cta-contact"
            >
              <Mail size={16} />
              Get in Touch
            </motion.a>
            <motion.a
              href={data.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#12d640] text-[#12d640] px-8 py-3 font-mono text-sm uppercase tracking-widest rounded-lg hover:bg-[#12d640]/10 transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="hero-cta-linkedin"
            >
              <Linkedin size={16} />
              LinkedIn
            </motion.a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="text-[#12d640]"
          >
            <ChevronDown size={32} />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Section Header Component
const SectionHeader = ({ title, subtitle, align = "left" }) => (
  <div className={`mb-12 ${align === "center" ? "text-center" : ""}`}>
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="font-mono text-xs tracking-[0.3em] uppercase mb-2 gradient-text"
    >
      {subtitle}
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight text-white"
    >
      {title}
    </motion.h2>
  </div>
);

// About Section with Profile Photo and Hydraulic Schematics - Fully Responsive
const PROFILE_IMAGE = "https://customer-assets.emergentagent.com/job_a6cf781b-8875-4a38-bf0c-caf85539da40/artifacts/pj36u80t_bunny_portfolio.jpg";

const AboutSection = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const [flowProgress, setFlowProgress] = useState(0);
  
  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (v) => setFlowProgress(v));
    return () => unsubscribe();
  }, [scrollYProgress]);

  return (
    <section
      id="about"
      ref={ref}
      className="py-16 sm:py-20 md:py-24 lg:py-32 relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#12d640]/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header - Centered */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeader title="About Me" subtitle="The Engineer" align="center" />
        </div>

        {/* Mobile Layout: Stack vertically */}
        {/* Tablet Layout: 2 columns (image + text, schematic below) */}
        {/* Desktop Layout: 3 columns */}
        
        <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8 xl:gap-12 lg:items-start">
          
          {/* Profile Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex justify-center lg:justify-start lg:col-span-3"
          >
            <div className="relative group w-48 sm:w-56 md:w-64 lg:w-full max-w-xs">
              {/* Decorative frame */}
              <div className="absolute -inset-2 sm:-inset-3 rounded-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }} />
              <div className="absolute -inset-1 sm:-inset-2 bg-[#0a0a0a] rounded-2xl" />
              
              {/* Image container */}
              <motion.div 
                className="relative rounded-xl overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src={PROFILE_IMAGE}
                  alt="Sreekanth Netha Thatikonda"
                  className="w-full h-auto object-cover rounded-xl"
                  style={{ aspectRatio: '1/1', objectPosition: 'center top' }}
                  loading="lazy"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 via-transparent to-transparent" />
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-5 space-y-4 sm:space-y-5"
          >
            {/* About Text */}
            <div className="space-y-3 sm:space-y-4">
              {data.about.split('\n\n').map((paragraph, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="text-[#aaa] leading-relaxed text-sm sm:text-base"
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>

            {/* Stats Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-3 gap-2 sm:gap-3 pt-4 sm:pt-6"
            >
              {[
                { value: "3.8", label: "GPA" },
                { value: "4+", label: "Years Exp" },
                { value: "6+", label: "Projects" }
              ].map((stat, i) => (
                <motion.div 
                  key={i} 
                  className="glass-card p-2 sm:p-3 text-center group hover:border-[#12d640]/50 transition-all duration-300"
                  whileHover={{ y: -3 }}
                >
                  <p className="font-heading text-lg sm:text-xl md:text-2xl font-semibold gradient-text">{stat.value}</p>
                  <p className="font-mono text-[10px] sm:text-xs text-[#666] uppercase tracking-wider group-hover:text-[#aaa] transition-colors">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-2 sm:gap-3 pt-2"
            >
              <a
                href={data.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-[#12d640]/50 text-[#12d640] hover:bg-[#12d640]/10 transition-all font-mono text-[10px] sm:text-xs"
              >
                <Linkedin size={12} className="sm:w-3.5 sm:h-3.5" /> LinkedIn
              </a>
              <a
                href={`mailto:${data.email}`}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-[#00d4ff]/50 text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all font-mono text-[10px] sm:text-xs"
              >
                <Mail size={12} className="sm:w-3.5 sm:h-3.5" /> Email Me
              </a>
            </motion.div>
          </motion.div>

          {/* Hydraulic Schematics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-4"
          >
            <div className="glass-card p-3 sm:p-4 relative overflow-hidden group">
              {/* Glow effect on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'radial-gradient(circle at center, rgba(18, 214, 64, 0.1) 0%, transparent 70%)' }} />
              
              {/* Title */}
              <div className="text-center mb-2">
                <p className="font-mono text-[10px] sm:text-xs text-[#12d640] uppercase tracking-widest">SSL/CTL Hydraulic System</p>
              </div>
              
              {/* Schematic - Responsive height */}
              <div className="relative h-[250px] sm:h-[300px] md:h-[320px] lg:h-[350px]">
                <HydraulicSchematics scrollProgress={flowProgress} />
              </div>
              
              {/* Caption */}
              <p className="text-center text-[#666] text-[10px] sm:text-xs font-mono mt-2">
                Interactive hydraulic flow diagram
              </p>
            </div>
          </motion.div>
        </div>

        {/* Mobile-only: Show schematic more prominently with swipe hint */}
        <div className="lg:hidden mt-2 text-center">
          <p className="text-[#666] text-[10px] font-mono">
            ↑ Scroll to see flow animation
          </p>
        </div>
      </div>
    </section>
  );
};

// Education Section
const EducationSection = ({ data }) => {
  return (
    <section
      id="education"
      className="py-24 sm:py-32 relative overflow-hidden"
      data-testid="education-section"
    >
      <div className="bg-grid" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeader title="Education" subtitle="Academic Journey" align="center" />
        <div className="space-y-8">
          {data.education.map((edu, index) => (
            <motion.div
              key={edu.id || index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="glass-card p-6 sm:p-8"
              data-testid={`education-card-${index}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(18, 214, 64, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)' }}>
                    <GraduationCap className="text-[#12d640]" size={28} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-2xl font-semibold uppercase tracking-tight gradient-text mb-1">
                    {edu.institution}
                  </h3>
                  <p className="text-white font-medium mb-2">
                    {edu.degree}
                    {edu.gpa && <span className="text-[#00d4ff] ml-2">{edu.gpa}</span>}
                  </p>
                  <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#aaa]">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {edu.period}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={14} /> {edu.location}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(edu.courses || []).map((course, i) => (
                      <span key={i} className="skill-pill">{course}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Experience Section with Animated Timeline Line
const ExperienceSection = ({ data }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const lineHeight = useSpring(useTransform(scrollYProgress, [0.1, 0.9], ["0%", "100%"]), {
    stiffness: 100,
    damping: 30
  });

  return (
    <section
      id="experience"
      ref={ref}
      className="py-24 sm:py-32 relative overflow-hidden"
      data-testid="experience-section"
    >
      {/* Background - Wireframe Excavator */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src={WIREFRAME_BG}
          alt="Wireframe Excavator"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeader title="Experience" subtitle="Professional Journey" />

        <div className="relative">
          {/* Static Background Line - passes through center of dots */}
          <div className="absolute left-[9px] lg:left-1/2 top-0 bottom-0 w-[2px] bg-[#333] transform lg:-translate-x-1/2" />
          
          {/* Animated Progress Line - passes through center of dots */}
          <motion.div 
            className="absolute left-[9px] lg:left-1/2 top-0 w-[2px] transform lg:-translate-x-1/2 origin-top"
            style={{ 
              height: lineHeight,
              background: 'linear-gradient(180deg, #12d640 0%, #00d4ff 100%)',
              boxShadow: '0 0 10px #12d640, 0 0 20px #12d640'
            }}
          />

          <div className="space-y-12">
            {data.experience.map((exp, index) => (
              <motion.div
                key={exp.id || index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1 }}
                className={`relative grid lg:grid-cols-2 gap-8 ${index % 2 === 1 ? 'lg:text-right' : ''}`}
                data-testid={`experience-card-${index}`}
              >
                {/* Timeline Node - line passes through center */}
                <motion.div 
                  className="absolute left-0 lg:left-1/2 w-5 h-5 rounded-full transform lg:-translate-x-1/2 top-2 z-10"
                  style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, type: "spring" }}
                >
                  <motion.div 
                    className="absolute inset-0 rounded-full"
                    animate={{ 
                      boxShadow: ['0 0 5px #12d640', '0 0 20px #12d640', '0 0 5px #12d640']
                    }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                </motion.div>

                <div className={`pl-10 lg:pl-0 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className={`glass-card p-6 sm:p-8 ${index % 2 === 1 ? 'lg:ml-8' : 'lg:mr-8'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(18, 214, 64, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)' }}>
                        <Briefcase className="text-[#12d640]" size={20} />
                      </div>
                      <div className={index % 2 === 1 ? 'lg:text-right flex-1' : 'flex-1'}>
                        <h3 className="font-heading text-xl font-semibold uppercase tracking-tight gradient-text">
                          {exp.company}
                        </h3>
                        <p className="text-white text-sm">{exp.role}</p>
                      </div>
                    </div>

                    <div className={`flex flex-wrap gap-4 mb-4 text-sm text-[#aaa] ${index % 2 === 1 ? 'lg:justify-end' : ''}`}>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {exp.period}
                      </span>
                      <span className="skill-pill">{exp.type}</span>
                    </div>

                    <ul className={`space-y-2 ${index % 2 === 1 ? 'lg:text-left' : ''}`}>
                      {(exp.highlights || []).map((highlight, i) => (
                        <li key={i} className="text-[#aaa] text-sm flex gap-2">
                          <span className="text-[#12d640] flex-shrink-0">&#8226;</span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = ({ data }) => {
  return (
    <section id="projects" className="py-24 sm:py-32 relative overflow-hidden" data-testid="projects-section">
      <div className="absolute inset-0 z-0">
        <img src={HYDRAULIC_BG} alt="Hydraulic Equipment" className="w-full h-full object-cover opacity-15" loading="lazy" />
        <div className="absolute inset-0 bg-[#0a0a0a]/90" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeader title="Projects" subtitle="Engineering Work" align="center" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.projects.map((project, index) => (
            <motion.a
              key={project.id || index}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="group glass-card p-6 transition-all duration-300 block"
              data-testid={`project-card-${index}`}
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(18, 214, 64, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)' }}>
                <Layers className="text-[#12d640]" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold uppercase tracking-tight text-white mb-2 group-hover:text-[#12d640] transition-colors">
                {project.title}
              </h3>
              <p className="text-[#aaa] text-sm mb-4 line-clamp-2">{project.description}</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {(project.tags || []).map((tag, i) => (
                  <span key={i} className="skill-pill text-xs">{tag}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-sm font-mono uppercase tracking-wider gradient-text group-hover:gap-4 transition-all">
                View Project <ExternalLink size={14} />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Skills Section
const SkillsSection = ({ data }) => {
  const skillCategories = [
    { title: "Languages", icon: Code, skills: data.skills.languages },
    { title: "Design & Simulation", icon: Settings, skills: data.skills.design },
    { title: "Databases & PLM", icon: Cpu, skills: data.skills.databases },
    { title: "Libraries", icon: Layers, skills: data.skills.libraries },
    { title: "Tools", icon: Wrench, skills: data.skills.tools }
  ];

  return (
    <section id="skills" className="py-24 sm:py-32 relative overflow-hidden" data-testid="skills-section">
      <div className="absolute inset-0 z-0">
        <img src={HYDRAULIC_BG} alt="Hydraulic Equipment" className="w-full h-full object-cover opacity-20" loading="lazy" />
        <div className="absolute inset-0 bg-[#0a0a0a]/90" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeader title="Skills" subtitle="Technical Expertise" align="center" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass-card p-6"
              data-testid={`skills-category-${index}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(18, 214, 64, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)' }}>
                  <category.icon className="text-[#12d640]" size={20} />
                </div>
                <h3 className="font-heading text-lg font-semibold uppercase tracking-tight gradient-text">
                  {category.title}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {(category.skills || []).map((skill, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    className="skill-pill"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Certifications Section
const CertificationsSection = ({ data }) => {
  if (!data.certifications || data.certifications.length === 0) {
    return null;
  }

  return (
    <section id="certifications" className="py-24 sm:py-32 relative overflow-hidden" data-testid="certifications-section">
      <div className="bg-grid" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <SectionHeader title="Certifications" subtitle="Professional Credentials" align="center" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.certifications.map((cert, index) => (
            <motion.a
              key={cert.id || index}
              href={cert.credential_url || data.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="group glass-card p-6 transition-all duration-300 block"
              data-testid={`certification-card-${index}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, rgba(18, 214, 64, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%)' }}>
                  <Award className="text-[#12d640]" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-semibold text-white mb-1 group-hover:text-[#12d640] transition-colors">
                    {cert.name}
                  </h3>
                  <p className="text-[#aaa] text-sm mb-2">{cert.issuer}</p>
                  {cert.issue_date && (
                    <p className="text-[#666] text-xs flex items-center gap-1">
                      <Calendar size={12} /> Issued {cert.issue_date}
                    </p>
                  )}
                </div>
                <ExternalLink className="text-[#666] group-hover:text-[#12d640] transition-colors flex-shrink-0" size={16} />
              </div>
            </motion.a>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <a
            href={data.linkedin + "details/certifications/"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#12d640] hover:text-[#00ff55] font-mono text-sm uppercase tracking-wider transition-colors"
          >
            View All on LinkedIn <ExternalLink size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = ({ data }) => {
  return (
    <section id="contact" className="py-24 sm:py-32 relative overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <SectionHeader title="Get in Touch" subtitle="Let's Connect" align="center" />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[#aaa] text-lg mb-12"
          >
            Interested in discussing hydraulic systems, engineering projects, or potential collaborations? 
            I'm always open to connecting with fellow engineers and industry professionals.
          </motion.p>

          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            <motion.a
              href={`mailto:${data.email}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5 }}
              className="glass-card p-6 group transition-all"
              data-testid="contact-email"
            >
              <Mail className="text-[#12d640] mx-auto mb-3 group-hover:scale-110 transition-transform" size={28} />
              <p className="font-mono text-xs text-[#666] uppercase tracking-wider mb-1">Email</p>
              <p className="text-white text-sm truncate">{data.email}</p>
            </motion.a>

            <motion.a
              href={data.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5 }}
              className="glass-card p-6 group transition-all"
              data-testid="contact-linkedin"
            >
              <Linkedin className="text-[#12d640] mx-auto mb-3 group-hover:scale-110 transition-transform" size={28} />
              <p className="font-mono text-xs text-[#666] uppercase tracking-wider mb-1">LinkedIn</p>
              <p className="text-white text-sm">Connect with me</p>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6"
              data-testid="contact-location"
            >
              <MapPin className="text-[#12d640] mx-auto mb-3" size={28} />
              <p className="font-mono text-xs text-[#666] uppercase tracking-wider mb-1">Location</p>
              <p className="text-white text-sm">{data.location}</p>
            </motion.div>
          </div>

          <motion.a
            href={`mailto:${data.email}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 px-10 py-4 font-mono text-sm uppercase tracking-widest font-medium text-black rounded-lg"
            style={{ background: 'linear-gradient(135deg, #12d640 0%, #00d4ff 100%)' }}
            data-testid="contact-cta"
          >
            <Mail size={18} />
            Send Me a Message
          </motion.a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = ({ data }) => (
  <footer className="py-8 border-t border-[#12d640]/20" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="font-mono text-xs text-[#666]">
          &copy; 2022 {data.name}. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <a href={data.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#666] hover:text-[#12d640] transition-colors" data-testid="footer-linkedin">
            <Linkedin size={18} />
          </a>
          <a href={`mailto:${data.email}`} className="text-[#666] hover:text-[#12d640] transition-colors" data-testid="footer-email">
            <Mail size={18} />
          </a>
        </div>
      </div>
    </div>
  </footer>
);

// Loading Screen Component
const LoadingScreen = () => (
  <div className="fixed inset-0 bg-[#0a0a0a] z-[9999] flex items-center justify-center">
    <div className="text-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="w-16 h-16 border-4 border-[#12d640]/20 border-t-[#12d640] rounded-full mx-auto mb-4"
      />
      <motion.p
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        className="text-[#12d640] font-mono text-sm"
      >
        Loading...
      </motion.p>
    </div>
  </div>
);

// Helper to merge API data with defaults
function mergePortfolioData(apiData, defaults) {
  return {
    ...defaults,
    about: apiData.about || defaults.about,
    education: apiData.education?.length > 0 ? apiData.education : defaults.education,
    experience: apiData.experience?.length > 0 ? apiData.experience : defaults.experience,
    projects: apiData.projects?.length > 0 ? apiData.projects : defaults.projects,
    skills: {
      languages: apiData.skills?.languages?.length > 0 ? apiData.skills.languages : defaults.skills.languages,
      design: apiData.skills?.design?.length > 0 ? apiData.skills.design : defaults.skills.design,
      databases: apiData.skills?.databases?.length > 0 ? apiData.skills.databases : defaults.skills.databases,
      libraries: apiData.skills?.libraries?.length > 0 ? apiData.skills.libraries : defaults.skills.libraries,
      tools: apiData.skills?.tools?.length > 0 ? apiData.skills.tools : defaults.skills.tools,
    },
    certifications: apiData.certifications?.length > 0 ? apiData.certifications : defaults.certifications
  };
}

// Main App Component
function App() {
  const [isLoading, setIsLoading] = useState(true);
  const { portfolioData: apiData, loading: dataLoading } = usePortfolio();

  useEffect(() => {
    // Preload the background image
    const img = new Image();
    img.onload = () => setIsLoading(false);
    img.onerror = () => setIsLoading(false);
    img.src = HYDRAULIC_BG;
    
    // Fallback timeout - don't wait more than 3 seconds
    const timeout = setTimeout(() => setIsLoading(false), 3000);
    return () => clearTimeout(timeout);
  }, []);

  // Merge API data with defaults (use API data if available, else use defaults)
  const portfolioData = mergePortfolioData(apiData, defaultPortfolioData);

  if (isLoading || dataLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="App bg-[#0a0a0a] min-h-screen">
      <div className="noise-overlay" />
      <div className="bg-grid" />
      <div className="orb orb-1" />
      <div className="orb orb-2" />
      <div className="orb orb-3" />
      
      <Navigation />
      
      <main>
        <HeroSection data={portfolioData} />
        <AboutSection data={portfolioData} />
        <div className="section-divider" />
        <EducationSection data={portfolioData} />
        <div className="section-divider" />
        <ExperienceSection data={portfolioData} />
        <div className="section-divider" />
        <ProjectsSection data={portfolioData} />
        <div className="section-divider" />
        <SkillsSection data={portfolioData} />
        <div className="section-divider" />
        <CertificationsSection data={portfolioData} />
        <div className="section-divider" />
        <ContactSection data={portfolioData} />
      </main>
      
      <Footer data={portfolioData} />
    </div>
  );
}

export default App;
